import type {
  AnalyticsProvider,
  OnUserChangeFunction,
  OnUserPropertiesChangeFunction,
  TrackEventFunction,
  TrackExceptionFunction,
} from "./types";

export class AnalyticsService {
  private initializedProviders: AnalyticsProvider[] = [];
  private callQueue: Array<() => void> = [];
  private isInitialized = false;

  constructor(private readonly providers: AnalyticsProvider[]) {
    const init = () => {
      this.providers.forEach((instance) => {
        if (instance.init()) {
          this.initializedProviders.push(instance);
        }
      });

      this.isInitialized = true;

      this.callQueue.forEach((queuedCall) => queuedCall());
      this.callQueue = [];
    };

    // Use requestIdleCallback if available (Chrome, Firefox, Edge, Opera);
    // Timeout ensures execution within 2 seconds even on busy pages;
    // Fall back to setTimeout(0) for Safari/iOS which don't support requestIdleCallback;
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      window.requestIdleCallback(init, { timeout: 2000 });
    } else {
      setTimeout(init, 0);
    }
  }

  private executeOrQueue(callback: (instance: AnalyticsProvider) => void) {
    if (!this.isInitialized) {
      this.callQueue.push(() => this.initializedProviders.forEach(callback));
      return;
    }

    this.initializedProviders.forEach(callback);
  }

  public trackEvent: TrackEventFunction = (eventName, properties) => {
    this.executeOrQueue((instance) => {
      instance.trackEvent(eventName, properties);
    });
  };

  public trackException: TrackExceptionFunction = (error, properties) => {
    this.executeOrQueue((instance) => {
      instance.trackException?.(error, properties);
    });
  };

  public trackUserChange: OnUserChangeFunction = (userData) => {
    this.executeOrQueue((instance) => {
      instance.onUserChange?.(userData);
    });
  };

  public trackUserPropertiesChange: OnUserPropertiesChangeFunction = (clientData) => {
    this.executeOrQueue((instance) => {
      instance.onUserPropertiesChange?.(clientData);
    });
  };
}
